import Vue from 'vue';
import makeStoreDebugAccessible from '../util/storeDebug';
import { HIGHLIGHT_CLASS } from './webHighlighter.store';

// All coordinates are relative to the viewport
export interface ICoordinates {
  // X coordinate for the click
  x: number;
  // Y coordinate for the click
  y: number;
  // Either the clicked element, or something relevant to it
  relatedElement: Element | null;
}

export enum ClickedFrom {
  HIGHLIGHT,
  MARKER,
  NOTES_LIST
}

interface PreviewState {
  highlightPreviewActive: boolean;
  coordinates: ICoordinates;
  clickedFrom: ClickedFrom | null;
}

const state: PreviewState = Vue.observable({
  highlightPreviewActive: false,
  coordinates: { x: 0, y: 0, relatedElement: null },
  clickedFrom: null
});

class NotePreviewStore {
  constructor() {
    if (process.env.NODE_ENV !== 'production') {
      makeStoreDebugAccessible('notes-preview-store', state);
    }
  }

  public get highlightPreviewActive(): boolean {
    return state.highlightPreviewActive;
  }

  public get coordinates(): ICoordinates {
    return state.coordinates;
  }

  public get clickedFrom(): ClickedFrom | null {
    return state.clickedFrom;
  }

  public showHighlightPreview(
    event: MouseEvent | TouchEvent,
    clickedFrom: ClickedFrom
  ): void {
    state.highlightPreviewActive = false;
    // Allow onDestroyed to be called before initializing new preview
    setTimeout(() => {
      state.clickedFrom = clickedFrom;
      state.coordinates = this.calculateCoordinatesForEvent(event);
      state.highlightPreviewActive = true;
    }, 16);
  }

  public closePreview(): void {
    state.highlightPreviewActive = false;
  }

  private closestRelevantElement(clickedElement: Element) {
    switch (this.clickedFrom) {
      case ClickedFrom.HIGHLIGHT: {
        const highlight = clickedElement.closest(`.${HIGHLIGHT_CLASS}`);
        if (highlight) {
          return highlight;
        }
        break;
      }
      case ClickedFrom.MARKER: {
        const marker = clickedElement.closest('.highlight-marker-wrapper');
        if (marker) {
          return marker;
        }
        break;
      }
      case ClickedFrom.NOTES_LIST: {
        const noteItem = clickedElement.closest('.js-note-item');
        if (noteItem) {
          return noteItem;
        }
        break;
      }
    }
    return clickedElement;
  }

  private calculateCoordinatesForEvent(
    event: MouseEvent | TouchEvent
  ): ICoordinates {
    const clickedElement = event.target as Element;
    const closestRelevant = this.closestRelevantElement(clickedElement);
    // default, if we haven't found something close.
    return NotePreviewStore.calculateCoordinatesRelativeToViewPort(
      closestRelevant,
      event
    );
  }

  private static calculateCoordinatesRelativeToViewPort(
    el: Element,
    event: MouseEvent | TouchEvent
  ): ICoordinates {
    if (!el) {
      // eslint-disable-next-line no-console
      console.warn(
        'Cannot set preview position, as element was not found. Returning a default value',
        el
      );
      return { x: 0, y: 0, relatedElement: null };
    }

    let x: number = 0;
    let y: number = 0;

    if (event instanceof MouseEvent) {
      x = event.clientX;
      y = event.clientY;
    } else if (event instanceof TouchEvent) {
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    }

    return {
      x,
      y,
      relatedElement: el
    };
  }
}

export default new NotePreviewStore();
